import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Reveal, Overline } from "../components/Reveal";
import { AudienceTag } from "../components/Tags";
import { CTASection } from "../components/CTASection";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KANJI = {
  working_holiday_guide: "旅",
  english_survival_guide: "話",
  workplace_english: "仕",
  cultural_guide: "橋",
  business_templates: "型",
  beginner_english: "始",
};

const ProductCover = ({ product }) => (
  <div className="cover-grain relative aspect-[3/4] bg-cream-surface border border-sand rounded-sm overflow-hidden flex flex-col justify-between p-5">
    <span className="absolute -right-4 -bottom-8 font-serif text-[11rem] leading-none text-navy/[0.07] select-none">
      {KANJI[product.lookup_key] || "橋"}
    </span>
    <div>
      <p className="font-mono text-[10px] tracking-[0.2em] text-coral-dark uppercase">{product.category}</p>
      <div className="mt-3 w-8 h-px bg-coral" />
    </div>
    <div className="relative">
      <h3 className="font-serif text-2xl leading-tight text-navy">{product.title}</h3>
      <p className="mt-2 font-mono text-[10px] tracking-[0.2em] text-charcoal/50 uppercase">
        Ruten Borealis
      </p>
    </div>
  </div>
);

export default function Store() {
  const [products, setProducts] = useState([]);
  const [filter, setFilter] = useState("All");
  const [buying, setBuying] = useState("");

  useEffect(() => {
    axios.get(`${API}/products`).then((r) => setProducts(r.data)).catch(() => toast.error("Could not load the store."));
  }, []);

  const categories = useMemo(() => ["All", ...new Set(products.map((p) => p.category))], [products]);
  const visible = filter === "All" ? products : products.filter((p) => p.category === filter);

  const buy = async (p) => {
    setBuying(p.lookup_key);
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, {
        lookup_key: p.lookup_key,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Checkout could not be started. Please try again.");
      setBuying("");
    }
  };

  return (
    <div data-testid="store-page" className="bg-cream pt-16">
      <section className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20 pt-20 sm:pt-28 pb-10">
        <Reveal>
          <Overline>STORE</Overline>
          <h1 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.12] tracking-tight text-navy max-w-3xl">
            Digital guides, written from both sides of the table.
          </h1>
          <p className="mt-8 text-base sm:text-lg leading-relaxed text-charcoal/75 max-w-2xl">
            Practical, instantly downloadable resources — for individuals preparing to cross,
            and for the businesses sending them.
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <div data-testid="store-filters" className="mt-10 flex flex-wrap gap-2">
            {categories.map((c) => (
              <button
                key={c}
                data-testid={`filter-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                onClick={() => setFilter(c)}
                className={`text-xs font-mono tracking-wider px-4 py-2 rounded-full border transition-colors duration-300 ${
                  filter === c
                    ? "bg-navy text-cream-surface border-navy"
                    : "border-sand text-charcoal/70 hover:border-navy hover:text-navy"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </Reveal>
      </section>

      <section className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20 pb-24">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {visible.map((p, i) => (
            <Reveal key={p.lookup_key} delay={i * 0.05}>
              <div id={`product-${p.lookup_key}`} data-testid={`product-${p.lookup_key}`} className="group">
                <div className="transition-transform duration-500 group-hover:-translate-y-1.5">
                  <ProductCover product={p} />
                </div>
                <div className="mt-5 flex items-start justify-between gap-4">
                  <div>
                    <AudienceTag audience={p.audience} testId={`product-${p.lookup_key}-tag`} />
                    <p className="mt-3 text-sm leading-relaxed text-charcoal/70">{p.description}</p>
                  </div>
                </div>
                <div className="mt-5 flex items-center justify-between border-t border-sand pt-4">
                  <span data-testid={`product-${p.lookup_key}-price`} className="font-serif text-2xl text-navy">
                    ${p.price} <span className="text-sm font-mono text-charcoal/50">{p.currency}</span>
                  </span>
                  <button
                    data-testid={`buy-${p.lookup_key}`}
                    onClick={() => buy(p)}
                    disabled={buying === p.lookup_key}
                    className="bg-coral hover:bg-coral-dark disabled:opacity-60 text-white text-sm font-medium px-6 py-2.5 rounded-full transition-colors duration-300"
                  >
                    {buying === p.lookup_key ? "Opening…" : "Buy now"}
                  </button>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
        <p className="mt-14 text-xs font-mono tracking-wider text-charcoal/50 text-center">
          SECURE CHECKOUT BY STRIPE · INSTANT DIGITAL DELIVERY · PRICES IN CAD
        </p>
      </section>

      <CTASection />
    </div>
  );
}
